import { useEffect, useMemo, useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import BikeCard from "../components/BikeCard";

const LICENCE_OPTIONS = ["all", "A1", "A2", "A"];
const TYPE_OPTIONS = ["all", "motorcycle", "scooter"];

export default function Catalog({ condition }) {
    const { t, lang } = useLang();
    const [bikes, setBikes] = useState([]);
    const [activeCat, setActiveCat] = useState("all");
    const [activeLicence, setActiveLicence] = useState("all");
    const [activeType, setActiveType] = useState("all");

    useEffect(() => {
        api.get("/bikes", { params: { condition } }).then((r) => setBikes(r.data || []));
    }, [condition]);

    const categories = useMemo(() => {
        const set = new Set(bikes.map((b) => b.category));
        return ["all", ...Array.from(set)];
    }, [bikes]);

    const filtered = bikes.filter((b) => {
        if (activeCat !== "all" && b.category !== activeCat) return false;
        if (activeLicence !== "all" && b.licence_class !== activeLicence) return false;
        if (activeType !== "all" && b.bike_type !== activeType) return false;
        return true;
    });
    const isUsed = condition === "used";

    return (
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28" data-testid={`catalog-${condition}`}>
            <p className="text-overline">
                {isUsed ? t.misc.catalog_overline_used : "Yamaha 2026 · Mallorca"}
            </p>
            <h1 className="mt-4 font-display text-5xl font-bold tracking-tighter sm:text-6xl lg:text-7xl">
                {isUsed ? t.sections.used_title : t.sections.new_title}
            </h1>
            <p className="mt-4 max-w-xl text-base text-neutral-500">
                {isUsed ? t.sections.used_desc : t.sections.new_desc}
            </p>

            {/* Filter rows */}
            <div className="mt-12 space-y-4" data-testid="catalog-filters">
                <FilterRow
                    label={t.bike.licence}
                    options={LICENCE_OPTIONS}
                    active={activeLicence}
                    onChange={setActiveLicence}
                    allLabel={t.misc.all}
                    testidPrefix="filter-licence"
                />
                <FilterRow
                    label={t.bike.type}
                    options={TYPE_OPTIONS}
                    active={activeType}
                    onChange={setActiveType}
                    testidPrefix="filter-type"
                    allLabel={t.misc.all}
                    formatLabel={(v) => (v === "motorcycle" ? t.misc.bike_moto : v === "scooter" ? t.misc.scooter : t.misc.all)}
                />
                <FilterRow
                    label={t.misc.category}
                    options={categories}
                    active={activeCat}
                    onChange={setActiveCat}
                    allLabel={t.misc.all}
                    testidPrefix="filter-cat"
                />
            </div>

            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map((b, i) => (
                    <BikeCard key={b.id} bike={b} index={i} />
                ))}
            </div>

            {filtered.length === 0 && (
                <p className="mt-16 text-center text-neutral-500 font-mono text-sm">
                    {t.sections.no_bikes}
                </p>
            )}
        </div>
    );
}

function FilterRow({ label, options, active, onChange, testidPrefix, formatLabel, allLabel = "All" }) {
    const fmt = formatLabel || ((v) => (v === "all" ? allLabel : v));
    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mr-2 min-w-[60px]">
                {label}
            </span>
            {options.map((opt) => (
                <button
                    key={opt}
                    onClick={() => onChange(opt)}
                    data-testid={`${testidPrefix}-${opt}`}
                    className={`px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] border transition-colors ${
                        active === opt
                            ? "border-black text-neutral-900 bg-black/5"
                            : "border-black/15 text-neutral-500 hover:text-neutral-900 hover:border-black/30"
                    }`}
                >
                    {fmt(opt)}
                </button>
            ))}
        </div>
    );
}
